import React, { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../utils/api';

const MaintenanceTracking = () => {
  const [formData, setFormData] = useState({
    protocolNumber: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [maintenance, setMaintenance] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMaintenance(null);
    
    if (!formData.protocolNumber || !formData.phone) {
      setError('Inserisci sia il numero di protocollo che il numero di telefono');
      return;
    }

    const { protocolNumber, phone } = formData;
    console.log("➡️ Chiamata API Tracking con:", protocolNumber, phone);
    
    setLoading(true);
    try {
      const res = await api.post('/maintenance/track', formData, {
        headers: {
          Authorization: undefined  // Forza l'esclusione dell'header Authorization
        }
      });
      setMaintenance(res.data);
    } catch (err) {
      console.error('Error tracking maintenance:', err);
      setError(err.response?.data?.msg || 'Errore nel recupero dei dati. Verifica che il numero di protocollo e il numero di telefono siano corretti.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'In Coda': 'default',
      'In Corso': 'primary',
      'Attesa Ricambi': 'warning',
      'Attesa Cliente': 'info',
      'Da Chiamare': 'secondary',
      'Da Ritirare': 'success',
      'Sollecitato': 'error',
      'Parcheggio': 'default',
      'Consegnato': 'success'
    };
    return statusColors[status] || 'default';
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Verifica Stato Manutenzione
          </Typography>
          <Typography variant="body1" align="center" sx={{ mb: 4 }}>
            Inserisci il numero di protocollo della manutenzione e il tuo numero di telefono per verificare lo stato della riparazione
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                required
                fullWidth
                id="protocolNumber"
                label="Numero Protocollo"
                name="protocolNumber"
                type="number"
                value={formData.protocolNumber}
                onChange={handleChange}
                variant="outlined"
              />
              <TextField
                required
                fullWidth
                id="phone"
                label="Numero di Telefono"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
                placeholder="Es. 3331234567"
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={!loading && <SearchIcon />}
                disabled={loading}
                sx={{ minWidth: '120px', height: { sm: '56px' } }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verifica'}
              </Button>
            </Box>
          </Box>

          {maintenance && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Dettagli Manutenzione #{maintenance.protocolNumber}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Stato Attuale
                    </Typography>
                    <Chip 
                      label={maintenance.status} 
                      color={getStatusColor(maintenance.status)}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Veicolo
                    </Typography>
                    <Typography variant="body1">
                      {maintenance.Vehicle ? `${maintenance.Vehicle.brand} ${maintenance.Vehicle.model} ${maintenance.Vehicle.color || ''}` : 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Descrizione Problema
                    </Typography>
                    <Typography variant="body1">
                      {maintenance.description}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Data Inizio
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(maintenance.startDate)}
                      </Typography>
                    </Box>
                    
                    {maintenance.endDate && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Data Fine
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(maintenance.endDate)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Sezione Costi */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Costo Riparazione
                    </Typography>
                    {maintenance.finalCost ? (
                      <Typography variant="body1">
                        €{parseFloat(maintenance.finalCost).toFixed(2)}
                      </Typography>
                    ) : maintenance.estimatedCost ? (
                      <Typography variant="body1">
                        €{parseFloat(maintenance.estimatedCost).toFixed(2)} (Stima)
                      </Typography>
                    ) : (
                      <Typography variant="body1">
                        Non ancora disponibile
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default MaintenanceTracking;